import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  CancelWorkOffer,
  getBundleOfferCount,
  getWorkOffersStateByBundleId,
  makeManualOffer,
  ManualOfferSearchForBundle,
  ManualOfferSearchRequirements,
  ManualOfferTalent,
  OffersStatus,
  OrderRowEvent,
  OrderWorkOffers,
  postCancelWorkOffers,
  searchManualOfferTalents,
  searchManualOfferTalentsForBundle,
  SendManualWorkOffer,
  SendWorkOffer,
  sendWorkOfferRequest,
  TalentWorkOfferFilters,
  TalentWorkOfferStatus,
  workLocationTalentPoolById,
  WorkLocationTalentPoolById,
  WorkLocationTalentPoolByIdFilters,
} from "./orderAPI";
import { TALENT_OFFER_OFFERED } from "./constants";
import { createQueryAsyncThunk } from "../../app/createApiAsyncThunk";

export type SliceState = {
  ids: [];
  entities: {};
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const workOffersAdapter = createEntityAdapter<OrderWorkOffers>();

export const cancelWorkOffers = createAsyncThunk<
  OrderRowEvent[],
  CancelWorkOffer,
  { state: RootState }
>(
  "workOffers/cancelWorkOffer",
  async (params: CancelWorkOffer, { requestId }) => {
    const res = await postCancelWorkOffers(params, { requestId });
    return res.data;
  },
);

export const sendManualOffer = createAsyncThunk<
  OrderRowEvent[],
  SendManualWorkOffer,
  { state: RootState }
>(
  "workOffers/sendManualOffer",
  async (params: SendManualWorkOffer, { requestId }) => {
    const res = await makeManualOffer(params, { requestId });
    return res.data;
  },
);

export const getWorkOffersState = createAsyncThunk<
  OrderWorkOffers,
  Omit<TalentWorkOfferFilters, "search">,
  { state: RootState }
>(
  "workOffers/getWorkOffersStateByBundleId",
  async (filters: Omit<TalentWorkOfferFilters, "search">) => {
    const res = await getWorkOffersStateByBundleId(filters.bundleId);
    return res.data;
  },
);

export const findManualOfferTalents = createAsyncThunk<
  ManualOfferTalent[],
  ManualOfferSearchRequirements,
  { state: RootState }
>(
  "workOffers/findManualOfferTalents",
  async (requirements: ManualOfferSearchRequirements) => {
    const res = await searchManualOfferTalents(requirements);
    return res.data;
  },
);

export const findManualOfferTalentsForBundle = createAsyncThunk<
  TalentWorkOfferStatus[],
  ManualOfferSearchForBundle,
  { state: RootState }
>(
  "workOffers/findManualOfferTalentsForBundle",
  async (params: ManualOfferSearchForBundle) => {
    const res = await searchManualOfferTalentsForBundle(params);
    return res.data;
  },
);

export const fetchWorkLocationTalentPoolById = createQueryAsyncThunk<
  WorkLocationTalentPoolById,
  WorkLocationTalentPoolByIdFilters
>("workOffers/fetchWorkLocationTalentPoolById", workLocationTalentPoolById);

export const fetchOrderRowsOfferCount = createAsyncThunk<
  OffersStatus,
  string,
  { state: RootState }
>("workOffers/fetchOrderRowsOfferStatus", async (bundleId: string) => {
  const res = await getBundleOfferCount(bundleId);
  return res;
});

export const sendWorkOfferToTalent = createAsyncThunk<
  Promise<any>,
  SendWorkOffer,
  { state: RootState }
>("workOffers/sendWorkOffer", async (payload: SendWorkOffer, { requestId }) => {
  const response = await sendWorkOfferRequest(payload, { requestId });
  return response.data;
});

export const workOffersSlice = createSlice({
  name: "workOffers",
  initialState: workOffersAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getWorkOffersState.pending, (state) => {
        state.status = "pending";
      })
      .addCase(getWorkOffersState.fulfilled, (state, action) => {
        workOffersAdapter.setAll(state, []);
        workOffersAdapter.setOne(state, {
          ...action.payload,
          id: action.meta.arg.bundleId,
        });
        state.status = "succeeded";
      })
      .addCase(getWorkOffersState.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(sendManualOffer.pending, (state) => {
        state.status = "pending";
      })
      .addCase(sendManualOffer.fulfilled, (state, action) => {
        const { bundleId, talentId, firstName, lastName } = action.meta.arg;
        // @ts-ignore
        const offers = state.entities[bundleId] as OrderWorkOffers;
        const newOffer = {
          manuallyOffered: action.payload[0].payload.manuallyOffered,
          id: talentId,
          firstName,
          lastName,
          workOfferStatus: TALENT_OFFER_OFFERED,
        };
        const updatedOffers = {
          ...offers,
          manuallyOffered: [...offers.manuallyOffered, newOffer],
        };
        workOffersAdapter.updateOne(state, {
          id: bundleId,
          changes: updatedOffers,
        });
        state.status = "succeeded";
      })
      .addCase(sendManualOffer.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(cancelWorkOffers.pending, (state) => {
        state.status = "pending";
      })
      .addCase(cancelWorkOffers.fulfilled, (state, action) => {
        const { bundleId } = action.meta.arg;
        // @ts-ignore
        const offers = state.entities[bundleId] as OrderWorkOffers;
        const updatedOffers = {
          ...offers,
          manuallyOffered: offers.manuallyOffered.filter(
            (offer) => !action.meta.arg.talentIds.includes(offer.id),
          ),
        };
        workOffersAdapter.updateOne(state, {
          id: bundleId,
          changes: updatedOffers,
        });
        state.status = "succeeded";
      })
      .addCase(cancelWorkOffers.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(findManualOfferTalentsForBundle.pending, (state) => {
        state.status = "pending";
      })
      .addCase(findManualOfferTalentsForBundle.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(findManualOfferTalentsForBundle.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      });
  },
});

export const {
  selectAll: selectAllWorkOffers,
  selectById: selectWorkOffersByBundleId,
} = workOffersAdapter.getSelectors((state: RootState) => state.workOffers);

export const workOffersReducer = workOffersSlice.reducer;
